'use client';

import { Globe } from 'lucide-react';
import { Button } from '@/app/_components/core/button';
import { SmartLink } from '@/app/_components/core/smart-link';
import { useLabel } from '@/app/_contexts/labels-context';
import { cn } from '@/app/_lib/utils';
import type { LanguageSwitcherProps } from './language-switcher.types';

export default function LanguageSwitcherComponent({
  languages,
  fullWidth,
  className = '',
}: LanguageSwitcherProps) {
  const notAvailableLabel = useLabel('languageNotAvailable', 'not available');
  const switchToLabel = useLabel('switchToLanguage', 'Switch to');

  // Show switcher if there are at least 2 languages in the system
  if (languages.length <= 1) {
    return null;
  }

  // Find the non-active language (the one to switch to)
  const otherLanguage = languages.find((lang) => lang.active !== 1);

  if (!otherLanguage) {
    return null;
  }

  // Disabled if not available OR no link
  const isDisabled = otherLanguage.available !== 1 || !otherLanguage.link;
  const label = otherLanguage.title || otherLanguage.navigationTitle;

  if (isDisabled) {
    return (
      <Button
        variant="ghost"
        size="sm"
        fullWidth={fullWidth}
        className={cn('font-normal', className)}
        disabled
        aria-label={`${label} (${notAvailableLabel})`}
      >
        <Globe className="size-4" />
        {label}
      </Button>
    );
  }

  return (
    <Button
      asChild
      variant="ghost"
      size="sm"
      fullWidth={fullWidth}
      className={cn('font-normal', className)}
    >
      <SmartLink
        href={otherLanguage.link}
        aria-label={`${switchToLabel} ${label}`}
        hrefLang={otherLanguage.hreflang}
        data-track-id="header-lang-switch"
      >
        <Globe className="size-4" />
        {label}
      </SmartLink>
    </Button>
  );
}

export { LanguageSwitcherComponent };
